import { fetchOpenAIText } from "../utils/openaiClient";

// Same per-level word-count/style calibration used by the glossary
// auto-generate feature and Essential Vocabulary — reused here so bulk
// generation meets the same CEFR-lexicographer bar instead of a bare
// "clear, student-friendly definition" with no enforced constraints.
const WD_LEVEL_SPECS = {
  A1: { maxWords: 10, style: 'Use A1 words only. One simple clause. No idioms. Avoid abstract terms.', example: 'big: large in size; quiet: making little sound' },
  A2: { maxWords: 14, style: 'Use A2 everyday words. One simple sentence. No relative clauses or advanced words.', example: 'borrow: take and return later; crowded: full of people' },
  B1: { maxWords: 18, style: 'Use B1 words. Clear neutral style. Avoid academic/rare terms.', example: 'flexible: can change or bend easily; efficient: doing work well without waste' },
  B2: { maxWords: 20, style: 'Use B2 words. You may use one relative clause. Keep concise.', example: 'sustainable: can continue without causing harm; justify: give good reasons for' },
  C1: { maxWords: 22, style: 'Advanced but concise academic tone (C1). Prefer precise, high-utility words.', example: 'ambiguous: having more than one possible meaning; facilitate: make a process easier' },
  C2: { maxWords: 22, style: 'Very advanced, precise and concise (C2). One sentence only.', example: 'ubiquitous: present everywhere; empirical: based on observed evidence' },
};

const wdEscapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const wdCountWords = (s) => String(s || '').trim().split(/\s+/).filter(Boolean).length;
const wdTooComplexFor = (s, lvl) => {
  if (lvl !== 'A1' && lvl !== 'A2') return false;
  const txt = String(s || '');
  if (/[,:;]/.test(txt)) return true;
  if (/(which|that|because|although|despite|furthermore|however|therefore)\b/i.test(txt)) return true;
  return false;
};

export function useWordDefinitionGeneratorLogic({
  wordDefinitionInput, setWordDefinitionInput, wordDefinitionLevel,
  setWordDefinitionLoading, generatedWordDefinitions, setGeneratedWordDefinitions,
  currentTab, activeEditor, onInsertToBoard,
  setShowWordDefinitionGenerator, setSelectedTemplate,
}) {
  // Regenerate a single definition that failed validation (used the target
  // word, too long, or too complex for the level) — same retry+refine
  // logic already proven in the glossary auto-generate feature.
  const wdRegenerateOne = async (word, spec) => {
    const targetRe = new RegExp(`\\b${wdEscapeRegExp(word)}\\b`, 'i');
    const buildPrompt = (strict = false) => `You are a CEFR-aware lexicographer. Write ONE short English definition that matches the target level.\n\nTarget word/phrase: "${word}"\nLevel: ${wordDefinitionLevel}\nGuidelines: ${spec.style}\nStyle examples for this level (do not copy, only mimic complexity): ${spec.example}\nHard constraints:\n- Do NOT use the target word (or any inflected form) in the definition\n- Exactly one sentence\n- Max ${spec.maxWords} words\n- Plain text only (no quotes, no markdown, no examples)\n- No meta phrases like "a word that means" or "term for"\n${strict ? '- STRICT retry: If uncertain, prefer a generic hypernym that fits the level.' : ''}`;

    let text = await fetchOpenAIText({ prompt: buildPrompt(false), temperature: 0.15, maxTokens: 120 });
    let firstLine = String(text || '').split('\n')[0].trim();
    if (targetRe.test(firstLine)) {
      text = await fetchOpenAIText({ prompt: buildPrompt(true), temperature: 0.1, maxTokens: 120 });
      firstLine = String(text || '').split('\n')[0].trim();
      if (targetRe.test(firstLine)) firstLine = firstLine.replace(targetRe, 'the term');
    }
    if (wdCountWords(firstLine) > spec.maxWords || wdTooComplexFor(firstLine, wordDefinitionLevel)) {
      const refinePrompt = `Rewrite this definition to strictly match CEFR ${wordDefinitionLevel} constraints. Keep meaning, but simplify or shorten. One sentence, max ${spec.maxWords} words, ${spec.style}. Do not use the target word.\nDefinition: ${firstLine}`;
      const refined = await fetchOpenAIText({ prompt: refinePrompt, temperature: 0.1, maxTokens: 120 });
      const refinedLine = String(refined || '').split('\n')[0].trim();
      if (refinedLine) firstLine = refinedLine;
    }
    return firstLine;
  };

  const handleWordDefinitionGenerate = async () => {
    if (!wordDefinitionInput.trim()) return;

    setWordDefinitionLoading(true);
    try {
      const spec = WD_LEVEL_SPECS[wordDefinitionLevel] || WD_LEVEL_SPECS.B1;
      const wordCount = wordDefinitionInput.split(/[\n,]+/).map(w => w.trim()).filter(Boolean).length;

      const prompt = `You are a CEFR-aware lexicographer building a word-definition matching exercise for English language learners, to the quality of a serious learner's dictionary entry — not a quick, generic guess.

Words: ${wordDefinitionInput}

CEFR level: ${wordDefinitionLevel} — ${spec.style}
Style examples for this level (do not copy, only mimic complexity): ${spec.example}

Hard rules for EVERY definition:
- Define EVERY word in the list — do not skip, merge, or reorder any. The output "pairs" array must have exactly ${wordCount} items, one per input word, in the same order.
- Do NOT use the target word itself (or any inflected form of it) anywhere in its own definition.
- Exactly one sentence per definition, max ${spec.maxWords} words.
- Plain text only — no quotes, no markdown, no examples, no meta phrases like "a word that means" or "term for".
- Write it the way a real dictionary would, in a student's own simpler words — not a mechanical synonym swap.

Return ONLY valid JSON in this format:
{
  "title": "Word-Definition Matching",
  "level": "${wordDefinitionLevel}",
  "pairs": [
    { "word": "word1", "definition": "definition1" }
  ]
}`;

      const content = await fetchOpenAIText({ prompt, temperature: 0.5, maxTokens: 900 });
      if (!content) {
        alert('Could not generate definitions — no response from the AI. Please try again.');
        return;
      }

      let jsonData;
      try {
        jsonData = JSON.parse(content.trim());
      } catch (e) {
        alert('Could not generate definitions — the AI response was invalid. Please try again.');
        return;
      }
      if (!Array.isArray(jsonData.pairs) || jsonData.pairs.length === 0) {
        alert('Could not generate definitions — the AI response was invalid. Please try again.');
        return;
      }

      // Validate each definition individually (doesn't leak the target
      // word, fits the level's length/complexity) and regenerate just the
      // ones that fail — cheaper than redoing the whole batch, and this
      // reuses the exact retry+refine logic already proven per-word.
      jsonData.pairs = await Promise.all(jsonData.pairs.map(async (pair) => {
        const word = String(pair.word || '').trim();
        if (!word) return pair;
        const targetRe = new RegExp(`\\b${wdEscapeRegExp(word)}\\b`, 'i');
        const needsFix = !pair.definition
          || targetRe.test(pair.definition)
          || wdCountWords(pair.definition) > spec.maxWords
          || wdTooComplexFor(pair.definition, wordDefinitionLevel);
        if (!needsFix) return pair;
        try {
          const fixed = await wdRegenerateOne(word, spec);
          return fixed ? { ...pair, definition: fixed } : pair;
        } catch (_) {
          return pair;
        }
      }));

      setGeneratedWordDefinitions(jsonData);
    } catch (e) {
      alert('Error generating definitions. Please try again.');
    } finally {
      setWordDefinitionLoading(false);
    }
  };

  const handleWordDefinitionInsert = () => {
    if (!generatedWordDefinitions) return;

    if (currentTab === 0) {
      if (!activeEditor) return;
      activeEditor.chain().focus().insertWordDefinitionExercise({
        pairs: generatedWordDefinitions.pairs,
        title: generatedWordDefinitions.title,
        level: generatedWordDefinitions.level
      }).run();
    } else {
      onInsertToBoard(generatedWordDefinitions, 'worddefinition');
    }

    setShowWordDefinitionGenerator(false);
    setWordDefinitionInput("");
    setGeneratedWordDefinitions(null);
    setSelectedTemplate(null);
  };

  const handleWordDefinitionCancel = () => {
    setShowWordDefinitionGenerator(false);
    setWordDefinitionInput("");
    setGeneratedWordDefinitions(null);
    setSelectedTemplate(null);
  };

  return { handleWordDefinitionGenerate, handleWordDefinitionInsert, handleWordDefinitionCancel };
}
